import React, { memo, useCallback, useMemo } from 'react';

import {
  any, arrayOf, bool, func, shape, string,
} from 'prop-types';

import {
  Date, Boolean, Enum, MultiSelect,
} from '../../CustomFields';
import l10n from '../../../shared/strings';
import { validateFieldValue } from './utils';
import TextFieldField from './fields/TextFieldField';
import DecimalField from './fields/DecimalField';
import DateTimeField from './fields/DateTimeField';
import UrlField from './fields/UrlField';
import MoneyField from './fields/MoneyField';
import RatingField from './fields/RatingField';
import MeasurementField from './fields/MeasurementField';
import LinkField from './fields/LinkField';
import ColorField from './fields/ColorField';
import RichTextField from './fields/RichTextField';
import JsonField from './fields/JsonField';
import ReferenceField from './fields/ReferenceField';
import MediaField from './fields/MediaField';

const MEASUREMENT_TYPES = ['weight', 'dimension', 'volume'];
const MEDIA_TYPES = ['file', 'image', 'video'];

function ProductField({
  data, disabled, readOnly, isDiff, value, onChange, inProgress, savedError, onValidationChange,
}) {
  const validate = useCallback(
    (v) => validateFieldValue(data.type, v, data),
    [data],
  );

  const handleChangeBoolean = useCallback((newValue) => {
    onChange(newValue, data);
  }, [data, onChange]);

  const handleChangeMultiSelect = useCallback((selected, onCancel, settings) => {
    onChange(selected, data, onCancel, settings);
  }, [data, onChange]);

  const placeholder = useMemo(() => {
    if (disabled) return '';
    const { type, multiple } = data;
    if (type === 'url') return l10n.PRODUCT_INFO.placeholders.linkUrl;
    if (type === 'link') return l10n.PRODUCT_INFO.placeholders.linkText;
    if (type === 'enum') return multiple ? l10n.PRODUCT_INFO.placeholders.selectValues : l10n.PRODUCT_INFO.placeholders.selectValue;
    if (type === 'int' || type === 'decimal' || type === 'rating' || type === 'datetime') return '';
    return l10n.PRODUCT_INFO.placeholders.inputValue;
  }, [data, disabled]);

  const fieldDisabled = disabled || inProgress;

  if (data.type === 'boolean') {
    return (
      <div key={data.title} className="customFieldBooleanWithModifiedField">
        <Boolean
          value={value}
          title={data.title}
          disabled={fieldDisabled}
          onChange={handleChangeBoolean}
        />
      </div>
    );
  }

  if (data.type === 'date') {
    return (
      <div key={data.title}>
        <Date
          value={value}
          customField={data}
          disabled={fieldDisabled}
          placeholder={value === null ? placeholder : undefined}
          onChange={onChange}
        />
      </div>
    );
  }

  if (data.type === 'int') {
    return (
      <TextFieldField
        commitAsNumber
        data={data}
        value={value === null ? '' : value}
        disabled={fieldDisabled}
        isDiff={isDiff}
        placeholder={placeholder}
        type="number"
        validate={validate}
        savedError={savedError}
        onValidationChange={onValidationChange}
        onChange={onChange}
      />
    );
  }

  if (data.type === 'input' || data.type === 'multiLine') {
    return (
      <TextFieldField
        data={data}
        disabled={fieldDisabled}
        isDiff={isDiff}
        value={value === null ? '' : value}
        placeholder={placeholder}
        multiline={data.type === 'multiLine'}
        validate={validate}
        savedError={savedError}
        onValidationChange={onValidationChange}
        onChange={onChange}
      />
    );
  }

  if (data.type === 'enum') {
    if (data.multiple) {
      return (
        <MultiSelect
          key={data.title}
          title={data.title}
          value={value}
          options={data.options}
          disabled={disabled}
          inProgress={inProgress}
          showDeletedOptions
          onChange={handleChangeMultiSelect}
        />
      );
    }

    return (
      <div key={data.title} className="customFieldSelectWithModifiedField">
        <Enum
          value={value}
          disabled={fieldDisabled}
          customField={data}
          placeholder={value === null ? placeholder : undefined}
          onChange={onChange}
        />
      </div>
    );
  }

  if (data.type === 'decimal') {
    return (
      <DecimalField
        data={data}
        value={value}
        disabled={fieldDisabled}
        isDiff={isDiff}
        placeholder={placeholder}
        validate={validate}
        savedError={savedError}
        onValidationChange={onValidationChange}
        onChange={onChange}
      />
    );
  }

  if (data.type === 'datetime') {
    return (
      <DateTimeField
        data={data}
        value={value}
        disabled={fieldDisabled}
        isDiff={isDiff}
        placeholder={placeholder}
        validate={validate}
        savedError={savedError}
        onValidationChange={onValidationChange}
        onChange={onChange}
      />
    );
  }

  if (data.type === 'url') {
    return (
      <UrlField
        data={data}
        value={value}
        disabled={fieldDisabled}
        isDiff={isDiff}
        placeholder={placeholder}
        validate={validate}
        savedError={savedError}
        onValidationChange={onValidationChange}
        onChange={onChange}
      />
    );
  }

  if (data.type === 'money') {
    return (
      <MoneyField
        data={data}
        value={value}
        disabled={fieldDisabled}
        isDiff={isDiff}
        validate={validate}
        savedError={savedError}
        onValidationChange={onValidationChange}
        onChange={onChange}
      />
    );
  }

  if (data.type === 'rating') {
    return (
      <RatingField
        data={data}
        value={value}
        disabled={fieldDisabled}
        isDiff={isDiff}
        validate={validate}
        savedError={savedError}
        onValidationChange={onValidationChange}
        onChange={onChange}
      />
    );
  }

  if (MEASUREMENT_TYPES.includes(data.type)) {
    return (
      <MeasurementField
        data={data}
        value={value}
        disabled={fieldDisabled}
        isDiff={isDiff}
        validate={validate}
        savedError={savedError}
        onValidationChange={onValidationChange}
        onChange={onChange}
      />
    );
  }

  if (data.type === 'link') {
    return (
      <LinkField
        data={data}
        value={value}
        disabled={fieldDisabled}
        isDiff={isDiff}
        placeholder={placeholder}
        validate={validate}
        savedError={savedError}
        onValidationChange={onValidationChange}
        onChange={onChange}
      />
    );
  }

  if (data.type === 'color') {
    return (
      <ColorField
        data={data}
        value={value}
        readOnly={readOnly}
        isDiff={isDiff}
        validate={validate}
        savedError={savedError}
        onValidationChange={onValidationChange}
        onChange={onChange}
      />
    );
  }

  if (data.type === 'richText') {
    return (
      <RichTextField
        data={data}
        value={value}
        disabled={fieldDisabled}
        isDiff={isDiff}
        placeholder={placeholder}
        className="m-b-16"
        onChange={onChange}
      />
    );
  }

  if (data.type === 'json') {
    return (
      <JsonField
        data={data}
        value={value}
        disabled={fieldDisabled}
        isDiff={isDiff}
        placeholder={placeholder}
        validate={validate}
        savedError={savedError}
        onValidationChange={onValidationChange}
        onChange={onChange}
      />
    );
  }

  if (data.type === 'reference') {
    return (
      <ReferenceField
        data={data}
        value={value}
        disabled={fieldDisabled}
        isDiff={isDiff}
        placeholder={placeholder}
        onChange={onChange}
      />
    );
  }

  if (MEDIA_TYPES.includes(data.type)) {
    return (
      <MediaField
        data={data}
        value={value}
        type={data.type}
        disabled={fieldDisabled}
        isDiff={isDiff}
        onChange={onChange}
      />
    );
  }

  return null;
}

ProductField.defaultProps = {
  disabled: false,
  readOnly: false,
  isDiff: false,
  onChange: Function.prototype,
  value: undefined,
  inProgress: false,
  savedError: null,
  onValidationChange: null,
};

ProductField.propTypes = {
  data: shape({
    visibility: string,
    title: string,
    type: string,
    options: arrayOf(string),
    pattern: string,
    patternDescription: string,
    writable: bool,
  }).isRequired,
  value: any,
  inProgress: bool,
  disabled: bool,
  readOnly: bool,
  isDiff: bool,
  savedError: string,
  onValidationChange: func,
  onChange: func,
};

export default memo(ProductField);
